import { getStore, updateStore } from 'core_main/store';
// eslint-disable-next-line import/no-cycle
import { importAndInitializePubNub } from 'core_main/globals/vendors';
import { handleError, LogLabel } from 'globals/errors';
import { subscribeToTypingIndicatorChannel } from 'core_main/helpers/typingIndicator';
import { fetchCurrentTrackingToken } from 'core_main/api/tracking';
import {
  inferStalePubnubCustomerIdFromSessions,
  rewriteSessionPubnubCustomerInStore,
} from './rewriteSessionPubnubCustomerInStore';

const RETRY_INTERVAL_MS = 200; // Interval between connection retry attempts
// Cover force-reinit ack wait (5s) plus a small buffer so typing resubscribe
// still succeeds when the connected ack arrives late rather than immediately.
const MAX_RETRIES = 30; // 30 * 200ms = 6 seconds total

const MAX_REKEY_ATTEMPTS = 3; // Attempts around importAndInitializePubNub
const REKEY_BACKOFF_BASE_MS = 400; // Linear backoff base (waits 400ms then 800ms)
// Steady-state token drift polls share this interval so identified widgets do
// not GET /tracking-tokens/current on every ~60s poll forever.
const DRIFT_CHECK_INTERVAL_MS = 5 * 60 * 1000;
// While a merge event marker is pending, check the tracking token more often
// so a missed session.update still recovers without waiting a full 5 minutes.
const POST_MERGE_DRIFT_CHECK_INTERVAL_MS = 15 * 1000;
// After this many poll-driven event recoveries while a marker is still
// pending, cool down before another force rebuild (avoids PubNub churn).
const MAX_EVENT_POLL_RECOVERIES = 3;

// Store timeout ID to prevent accumulation of retry timers from multiple re-keys
let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
let lastDriftCheckAt: number | null = null;
let lastExhaustedEventRekeyAt: number | null = null;
let eventPollRecoveryCount = 0;

// Single-flight guard; coalesce concurrent calls to the latest target customer.
let inFlightRekey: Promise<void> | null = null;
let coalescedTargetCustomerId: string | null = null;
let executingTargetCustomerId: string | null = null;
let eventCarriedCustomerId: string | null = null;
let eventCarriedFromCustomerId: string | null = null;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export type RekeyRealtimeSource = 'event' | 'drift';

export interface RekeyRealtimeOptions {
  source?: RekeyRealtimeSource;
  /** Authoritative pre-merge customer when source is 'event'. */
  fromCustomerId?: string;
}

/**
 * Re-subscribes to typing indicator channels for all active conversations after a customer merge.
 * This ensures that typing indicators continue to work with the new customer ID.
 *
 * When `expectedCustomerId` is provided, waits for both `isPubNubConnected === true`
 * AND `store.customerId === expectedCustomerId` before subscribing. This prevents
 * a race where the re-key re-init is still in flight (so the store/connection have not
 * yet settled on the surviving customer) — without this guard, the channel would be
 * built from the stale customer ID and miss every typing signal the Go publisher
 * targets at the new customer.
 */
export const resubscribeTypingIndicatorChannels = (
  expectedCustomerId?: string,
) => {
  // Clear any existing retry timeout to prevent accumulation
  if (retryTimeoutId !== null) {
    clearTimeout(retryTimeoutId);
    retryTimeoutId = null;
  }

  const store = getStore();
  const sessions = store?.sessions || {};

  // Get all conversation IDs from sessions
  const conversationIds = Object.keys(sessions);

  if (conversationIds.length === 0) {
    return;
  }

  // Re-subscribe typing indicator channels for all active conversations
  // eslint-disable-next-line import/no-cycle
  importAndInitializePubNub()
    .then((pubnub) => {
      // Wait for PubNub to be connected and (if given) the store to reflect
      // the post-merge customer before subscribing.
      let retryCount = 0;

      const checkConnection = () => {
        const currentStore = getStore();
        const isConnected = !!currentStore?.isPubNubConnected;
        const customerMatches =
          !expectedCustomerId ||
          currentStore?.customerId === expectedCustomerId;

        if (isConnected && customerMatches) {
          // Clear retry timeout since we're done
          if (retryTimeoutId !== null) {
            clearTimeout(retryTimeoutId);
            retryTimeoutId = null;
          }
          // Subscribe to typing indicator channels for all conversations
          Promise.all(
            conversationIds.map((conversationId) =>
              subscribeToTypingIndicatorChannel(conversationId, pubnub),
            ),
          ).catch((error) => {
            handleError(error, {
              message:
                'Failed to re-subscribe typing indicator channels after customer merge',
            });
          });
        } else if (retryCount < MAX_RETRIES) {
          // Retry after a short delay if not yet in the expected post-merge state
          retryCount++;
          retryTimeoutId = setTimeout(checkConnection, RETRY_INTERVAL_MS);
        } else {
          // Clear retry timeout since we're done (max retries reached)
          if (retryTimeoutId !== null) {
            clearTimeout(retryTimeoutId);
            retryTimeoutId = null;
          }
          handleError(
            new Error(
              `PubNub not in expected post-merge state after ${MAX_RETRIES} retries (isPubNubConnected=${isConnected} customerMatches=${customerMatches})`,
            ),
            {
              message:
                'Failed to re-subscribe typing indicator channels - PubNub not connected with expected customer',
            },
          );
        }
      };

      checkConnection();
    })
    .catch((error) => {
      // Clear retry timeout on error
      if (retryTimeoutId !== null) {
        clearTimeout(retryTimeoutId);
        retryTimeoutId = null;
      }
      handleError(error, {
        message:
          'Failed to initialize PubNub for re-subscribing typing indicator channels',
      });
    });
};

/**
 * True when the store still reflects the expected re-key source or the target.
 * Aborts an in-flight re-key if login/logout/stop replaced identity mid-flight.
 */
const isRekeyIdentityStillValid = (
  targetCustomerId: string,
  expectedFromCustomerId: string | null,
): boolean => {
  const current = getStore().customerId;
  if (current === targetCustomerId) return true;
  if (expectedFromCustomerId && current === expectedFromCustomerId) return true;
  if (!expectedFromCustomerId && !current) return true;
  return false;
};

/**
 * Re-keys the client's realtime to the surviving customer after a merge.
 *
 * The merge `session.update` event carries the authoritative surviving customer id. This path
 * previously called `login()`, which re-derives the customer by re-fetching the tracking token —
 * an async backend round-trip that, immediately after a merge, frequently still resolves to the
 * pre-merge customer (read-after-write lag). The store then stayed on the old id, PubNub never
 * re-subscribed to the survivor's channels, and realtime stayed silent until a manual refresh.
 *
 * Instead, re-initialize PubNub for the surviving id (with forceReinitialize). Identity is
 * committed once that initialization returns successfully — independent of whether the
 * subscribe acknowledgement has already arrived — then missed messages from the re-key gap
 * are fetched and replayed. Transport readiness must not gate the authoritative survivor id;
 * a delayed ack (or health-check reconnect) can still flip `isPubNubConnected` later.
 *
 * `isPubNubConnected` is cleared only after org/identity guards pass: a stale event or
 * mid-flight identity change must not mark an otherwise healthy connection disconnected.
 * Force re-init itself bypasses the connected short-circuit; clearing the flag still keeps
 * the store honest across teardown/rebuild.
 */
const isNonTransientRekeyAbort = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : '';
  return (
    message.includes('without an orgId') ||
    message.includes('customer identity changed')
  );
};

const attemptRekeyToCustomer = async (
  targetCustomerId: string,
): Promise<void> => {
  executingTargetCustomerId = targetCustomerId;
  const { orgId, customerId: sourceAtStart } = getStore();
  // Prefer the event-recorded source when this target is the event survivor;
  // otherwise use the store identity at attempt start (drift path).
  const expectedFromCustomerId =
    eventCarriedCustomerId === targetCustomerId
      ? eventCarriedFromCustomerId
      : sourceAtStart || null;
  let lastError: unknown;

  try {
    // Missing org is not transient — fail once without touching connection state.
    // Still stamp exhaustion so poll-driven retries can enter the same cooldown
    // as a normal exhausted cycle (avoids handleError on every poll forever).
    if (!orgId) {
      lastExhaustedEventRekeyAt = Date.now();
      handleError(new Error('Cannot re-key realtime without an orgId'), {
        message: 'Failed to re-key realtime to surviving customer after merge',
      });
      return;
    }

    for (let attempt = 1; attempt <= MAX_REKEY_ATTEMPTS; attempt++) {
      try {
        if (
          !isRekeyIdentityStillValid(targetCustomerId, expectedFromCustomerId)
        ) {
          throw new Error(
            'Realtime re-key aborted: customer identity changed during attempt',
          );
        }

        // Guards passed — drop the connected flag before forced rebuild so we
        // never claim connected across clearPubNub / subscribe.
        updateStore('isPubNubConnected', false);

        // Auth/setup failures reject above and retry. Force re-init may return
        // after the ack waiter times out while still disconnected (PAM race).
        // Do not commit survivor identity until PubNub is actually connected —
        // otherwise the widget falls through to the poller until refresh.
        // eslint-disable-next-line import/no-cycle, no-await-in-loop
        await importAndInitializePubNub({
          customerId: targetCustomerId,
          orgId,
          forceReinitialize: true,
        });

        if (!getStore().isPubNubConnected) {
          throw new Error(
            'Realtime re-key subscribe did not connect for surviving customer',
          );
        }

        if (
          !isRekeyIdentityStillValid(targetCustomerId, expectedFromCustomerId)
        ) {
          updateStore('isPubNubConnected', false);
          throw new Error(
            'Realtime re-key aborted: customer identity changed before commit',
          );
        }
        updateStore('customerId', targetCustomerId);
        // session.update often stores pre-recustomer channel names (cust-source).
        // Rewrite them to the survivor before typing / mark-read / history use them.
        const fromCustomerId =
          expectedFromCustomerId ||
          inferStalePubnubCustomerIdFromSessions(targetCustomerId);
        if (fromCustomerId) {
          rewriteSessionPubnubCustomerInStore(fromCustomerId, targetCustomerId);
        }
        resubscribeTypingIndicatorChannels(targetCustomerId);

        // Replay messages published while the old subscription was torn down and
        // the new one was not yet receiving. PubNub history often 403s on the
        // pre-merge channel after identity flip; REST session catch-up covers
        // that gap. Non-fatal: the poller remains the last-resort safety net.
        try {
          // eslint-disable-next-line import/no-cycle, no-await-in-loop
          const { markUndeliveredMessagesAsDelivered } = await import(
            /* webpackChunkName: "pubnub-message-recovery" */ './markUndeliveredMessages'
          );
          // eslint-disable-next-line no-await-in-loop
          await markUndeliveredMessagesAsDelivered(true);
        } catch (recoveryError) {
          handleError(recoveryError, {
            message:
              'Failed to recover undelivered messages after realtime re-key',
            labels: [LogLabel.PUBNUB_RECOVERY],
          });
        }

        try {
          // eslint-disable-next-line import/no-cycle, no-await-in-loop
          const { catchUpSessionMessagesAfterRekey } = await import(
            /* webpackChunkName: "pubnub-message-recovery" */ './catchUpSessionMessagesAfterRekey'
          );
          // eslint-disable-next-line no-await-in-loop
          await catchUpSessionMessagesAfterRekey();
        } catch (restRecoveryError) {
          handleError(restRecoveryError, {
            message: 'Failed REST session catch-up after realtime re-key',
            labels: [LogLabel.PUBNUB_RECOVERY],
          });
        }

        // Allow the next poll to fetch the token once for marker convergence
        // without waiting out a pre-event throttle window.
        lastExhaustedEventRekeyAt = null;
        eventPollRecoveryCount = 0;
        lastDriftCheckAt = null;
        return;
      } catch (error) {
        lastError = error;
        // Identity/org aborts will not improve on retry; stop immediately.
        if (isNonTransientRekeyAbort(error) || attempt >= MAX_REKEY_ATTEMPTS) {
          break;
        }
        // eslint-disable-next-line no-await-in-loop
        await sleep(REKEY_BACKOFF_BASE_MS * attempt);
      }
    }

    lastExhaustedEventRekeyAt = Date.now();
    handleError(lastError, {
      message: 'Failed to re-key realtime to surviving customer after merge',
    });
  } finally {
    if (executingTargetCustomerId === targetCustomerId) {
      executingTargetCustomerId = null;
    }
  }
};

const executeRekeyChain = (): Promise<void> =>
  Promise.resolve().then(async function drainCoalescedTargets() {
    while (coalescedTargetCustomerId) {
      const target = coalescedTargetCustomerId;
      coalescedTargetCustomerId = null;
      // eslint-disable-next-line no-await-in-loop
      await attemptRekeyToCustomer(target);
    }
  });

export const rekeyRealtimeToCustomer = (
  targetCustomerId: string,
  options: RekeyRealtimeOptions = {},
): Promise<void> => {
  if (options.source === 'event') {
    const nextFromCustomerId =
      options.fromCustomerId || getStore().customerId || null;
    const isFreshEvent =
      eventCarriedCustomerId !== targetCustomerId ||
      eventCarriedFromCustomerId !== nextFromCustomerId;
    eventCarriedCustomerId = targetCustomerId;
    // Prefer the event's authoritative before-id over the current store, which
    // may already sit on an intermediate survivor during A→B→C chains.
    eventCarriedFromCustomerId = nextFromCustomerId;
    // Only a new merge event resets poll-recovery budget — poll retries of the
    // same pending marker must keep the counter so the cooldown can engage.
    if (isFreshEvent) {
      lastExhaustedEventRekeyAt = null;
      eventPollRecoveryCount = 0;
    }
  }

  if (executingTargetCustomerId === targetCustomerId) {
    return inFlightRekey ?? Promise.resolve();
  }

  if (
    options.source === 'drift' &&
    executingTargetCustomerId &&
    inFlightRekey &&
    targetCustomerId !== executingTargetCustomerId
  ) {
    return inFlightRekey;
  }

  coalescedTargetCustomerId = targetCustomerId;

  if (!inFlightRekey) {
    inFlightRekey = executeRekeyChain().finally(() => {
      inFlightRekey = null;
    });
  }

  return inFlightRekey;
};

export const recoverRealtimeIdentityIfDrifted = async (): Promise<void> => {
  try {
    if (inFlightRekey) {
      await inFlightRekey;
    }

    const store = getStore();
    // Anonymous sessions have no customer to drift from; nothing to recover.
    if (!store.customerId) return;
    if (
      eventCarriedCustomerId &&
      store.customerId !== eventCarriedCustomerId &&
      store.customerId !== eventCarriedFromCustomerId
    ) {
      eventCarriedCustomerId = null;
      eventCarriedFromCustomerId = null;
    }

    const now = Date.now();

    // Retry a pending event target before any throttle / token fetch. A recent
    // successful token check must not delay authoritative survivor recovery.
    // Cap poll-driven force rebuilds: after MAX_EVENT_POLL_RECOVERIES exhausted
    // cycles, cool down so a permanently stuck grant cannot rebuild PubNub on
    // every ~60s poll forever.
    if (eventCarriedCustomerId && store.customerId !== eventCarriedCustomerId) {
      const eventRekeyCoolingDown =
        eventPollRecoveryCount >= MAX_EVENT_POLL_RECOVERIES &&
        lastExhaustedEventRekeyAt !== null &&
        now - lastExhaustedEventRekeyAt < DRIFT_CHECK_INTERVAL_MS;
      if (!eventRekeyCoolingDown) {
        if (eventPollRecoveryCount >= MAX_EVENT_POLL_RECOVERIES) {
          eventPollRecoveryCount = 0;
        }
        eventPollRecoveryCount += 1;
        console.info('[kustomer] realtime identity drift detected; re-keying');
        await rekeyRealtimeToCustomer(eventCarriedCustomerId, {
          source: 'event',
          fromCustomerId: eventCarriedFromCustomerId || undefined,
        });
        return;
      }
      // Fall through to the throttled token path during cooldown.
    }

    // Steady-state token drift is rate-limited at 5m; pending merge markers use
    // a shorter cadence so Slow-3G / missed-event gaps heal faster.
    const driftCheckIntervalMs = eventCarriedCustomerId
      ? POST_MERGE_DRIFT_CHECK_INTERVAL_MS
      : DRIFT_CHECK_INTERVAL_MS;
    if (
      lastDriftCheckAt !== null &&
      now - lastDriftCheckAt < driftCheckIntervalMs
    ) {
      return;
    }

    const response = await fetchCurrentTrackingToken();
    lastDriftCheckAt = Date.now();
    const tokenCustomerId = response?.data?.relationships?.customer?.data?.id;

    // Event-marker lifecycle once the store has committed to the survivor:
    // - token matches survivor → converged; clear markers
    // - token still the pre-merge source → read-after-write lag; keep marker
    // - token is a third id → missed B→C (etc.); clear marker so drift owns it
    if (
      eventCarriedCustomerId &&
      store.customerId === eventCarriedCustomerId &&
      tokenCustomerId
    ) {
      if (tokenCustomerId === eventCarriedCustomerId) {
        eventCarriedCustomerId = null;
        eventCarriedFromCustomerId = null;
      } else if (
        !eventCarriedFromCustomerId ||
        tokenCustomerId !== eventCarriedFromCustomerId
      ) {
        eventCarriedCustomerId = null;
        eventCarriedFromCustomerId = null;
      }
    }

    if (tokenCustomerId && tokenCustomerId !== store.customerId) {
      const targetCustomerId = eventCarriedCustomerId || tokenCustomerId;
      if (targetCustomerId === store.customerId) {
        return;
      }
      // Without an event marker, the tracking-token relationship is
      // authoritative (including anonymous→identified merges whose survivor
      // has an older ObjectId). An active event marker already protects
      // read-after-write lag above.
      console.info('[kustomer] realtime identity drift detected; re-keying');
      await rekeyRealtimeToCustomer(targetCustomerId, {
        source: eventCarriedCustomerId ? 'event' : 'drift',
        fromCustomerId: eventCarriedCustomerId
          ? eventCarriedFromCustomerId || undefined
          : store.customerId,
      });
    }
  } catch (error) {
    handleError(error, {
      message: 'Failed to check/recover realtime identity drift',
    });
  }
};
